import { useMemo, useCallback, useRef } from 'react';
import { Typography, Paper } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { useTranslation } from 'react-i18next';
import { getDetectionColor } from '../Utilities';
import { isMobile } from 'react-device-detect';

function formatDateLabel(label, isPerImage) {
    if (isPerImage && label?.includes('T')) {
        const [datePart, timePart] = label.split('T')
        const [y, m, d] = datePart.split('-')
        return `${d}.${m}.${y.slice(2)} ${timePart}`
    }
    const dateStr = String(label).substring(0, 10)
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [y, m, d] = dateStr.split('-')
        return `${d}.${m}.${y.slice(2)}`
    }
    return label
}

function DomTooltip() {
    return (
        <div
            data-tooltip-root=""
            style={{
                maxWidth: 280,
                background: 'rgba(30,30,50,0.95)',
                color: 'white',
                borderRadius: 4,
                overflow: 'hidden',
                pointerEvents: 'none',
                boxShadow: '0 3px 6px rgba(0,0,0,0.3)'
            }}
        >
            <div style={{ padding: '8px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                    <span data-dot="" style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, display: 'inline-block' }} />
                    <span data-type="" style={{ fontWeight: 'bold', fontSize: 13 }} />
                </div>
                <div data-date="" style={{ color: '#ccc', fontSize: 12 }} />
                <div data-count="" style={{ color: '#ccc', fontSize: 12 }} />
            </div>
            <img data-img="" alt="" style={{ width: '100%', display: 'none' }} onError={(e) => { e.target.style.display = 'none' }} />
        </div>
    )
}

export default function DetectionChart({ data, enabledTypes, onDayZoom, onBarClick, baseUrl, translateType, chartHeight }) {

    const { t } = useTranslation()

    const containerRef = useRef(null)
    const stateRef = useRef({ label: null, type: null, cursor: 'default' })
    const barHandlersRef = useRef({})

    const chartData = useMemo(() => {
        if (!data?.dates) return []
        return data.dates.map((value, index) => ({
            date: value,
            _img: data.image_filenames?.[index] || null,
            ...Object.fromEntries(data.object_types.map(elem => [elem, data.daily_counts[elem][index]]))
        }))
    }, [data])

    const enabledTypesArray = useMemo(() => {
        if (!data?.object_types) return []
        return data.object_types.filter(elem => enabledTypes.has(elem))
    }, [data, enabledTypes])

    // dates with visible bar segments (for tooltip display)
    const datesWithData = useMemo(() => {
        const set = new Set()
        for (const elem of chartData) {
            if (enabledTypesArray.some(type => (elem[type] || 0) > 0)) set.add(elem.date)
        }
        return set
    }, [chartData, enabledTypesArray])

    // all clickable dates: in per_image mode every date is clickable (empty images open viewer)
    const clickableDates = useMemo(() => {
        if (data?.resolution === 'per_image') return new Set(chartData.map(elem => elem.date))
        return datesWithData
    }, [data?.resolution, chartData, datesWithData])

    const dataByDate = useMemo(() => {
        const map = {}
        for (const elem of chartData) map[elem.date] = elem
        return map
    }, [chartData])

    const formatXAxis = useCallback((value) => {
        if (!value) return ''
        const dateString = String(value)
        if (data?.resolution === 'per_image' && dateString.includes('T')) return dateString.split('T')[1]
        if (dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
            const parts = dateString.substring(0, 10).split('-')
            return `${parts[2]}.${parts[1]}`
        }
        return dateString
    }, [data?.resolution])

    const handleClick = useCallback((chartEvent) => {
        if (!chartEvent?.activeLabel) return
        if (!clickableDates.has(chartEvent.activeLabel)) return
        const dateString = String(chartEvent.activeLabel).substring(0, 10)
        if (data?.resolution === 'per_image') {
            // over a specific bar segment -> pass that type, over empty area -> pass null (= all types)
            onBarClick?.(dateString, chartEvent.activeLabel, isOverBarRef.current ? hoveredTypeRef.current : null)
        } else {
            onDayZoom?.(dateString)
        }
    }, [data?.resolution, onBarClick, onDayZoom, clickableDates])

    // update tooltip DOM without re-render
    const updateTooltipDOM = useCallback((label, hoveredType) => {
        const container = containerRef.current
        if (!container) return

        const root = container.querySelector('[data-tooltip-root]')
        if (!root) return

        if (!label) {
            root.parentElement.style.visibility = 'hidden'
            return
        }

        const entry = dataByDate[label]
        if (!entry) { root.parentElement.style.visibility = 'hidden'; return }

        let typeKey = hoveredType && (entry[hoveredType] || 0) > 0 ? hoveredType : null
        if (!typeKey) typeKey = enabledTypesArray.find(elem => (entry[elem] || 0) > 0)
        if (!typeKey) { root.parentElement.style.visibility = 'hidden'; return }

        const count = entry[typeKey] || 0
        const state = stateRef.current
        if (state.label === label && state.type === typeKey) {
            root.parentElement.style.visibility = 'visible'
            return
        }
        state.label = label
        state.type = typeKey

        const isPerImage = data?.resolution === 'per_image'
        const color = getDetectionColor(typeKey)
        const dateLabel = isPerImage ? t('pages.dashboard.timestamp') : t('pages.dashboard.date')

        root.querySelector('[data-dot]').style.backgroundColor = color
        root.querySelector('[data-type]').textContent = translateType(typeKey)
        root.querySelector('[data-date]').textContent = `${dateLabel}: ${formatDateLabel(label, isPerImage)}`
        root.querySelector('[data-count]').textContent = `${t('pages.dashboard.count')}: ${count}`

        const img = root.querySelector('[data-img]')
        const filename = entry._img
        if (filename && baseUrl) {
            const src = `${baseUrl}${filename}`
            if (img.getAttribute('src') !== src) {
                img.style.display = 'block'
                img.src = src
            }
        } else {
            img.style.display = 'none'
        }

        root.parentElement.style.visibility = 'visible'
    }, [dataByDate, enabledTypesArray, data?.resolution, translateType, baseUrl, t])

    // reset cached state when data changes (drill-down, reset, filter change)
    const dataIdRef = useRef(null)
    if (data !== dataIdRef.current) {
        dataIdRef.current = data
        stateRef.current = { label: null, type: null, cursor: 'default' }
    }

    // recharts handlers — use refs to avoid re-renders
    const hoveredTypeRef = useRef(null)
    const isOverBarRef = useRef(false)

    const handleMouseMove = useCallback((chartEvent) => {
        const label = chartEvent?.activeLabel || null
        const isClickable = label && clickableDates.has(label)
        const hasData = label && datesWithData.has(label)
        const overBar = isOverBarRef.current && hasData

        // pointer cursor on any clickable column
        const next = isClickable ? 'pointer' : 'default'
        if (stateRef.current.cursor !== next) {
            stateRef.current.cursor = next
            const svg = containerRef.current?.querySelector('.recharts-wrapper')
            if (svg) svg.style.cursor = next
        }

        // tooltip only when hovering directly over a bar segment
        updateTooltipDOM(overBar ? label : null, hoveredTypeRef.current)
    }, [clickableDates, datesWithData, updateTooltipDOM])

    const handleMouseLeave = useCallback(() => {
        stateRef.current.label = null
        stateRef.current.type = null
        hoveredTypeRef.current = null
        isOverBarRef.current = false
        const root = containerRef.current?.querySelector('[data-tooltip-root]')
        if (root) root.parentElement.style.visibility = 'hidden'
        const svg = containerRef.current?.querySelector('.recharts-wrapper')
        if (svg) svg.style.cursor = 'default'
    }, [])

    // mobile: tap a bar directly fires onClick on first tap (no chart-level two-tap delay)
    const handleBarTap = useCallback((barData) => {
        const dateValue = barData?.payload?.date
        if (!dateValue) return
        handleClick({ activeLabel: dateValue })
    }, [handleClick])

    // per-type enter/leave handlers - also track whether cursor is over a visible bar
    const getBarHandlers = useCallback((typeKey) => {
        if (!barHandlersRef.current[typeKey]) {
            barHandlersRef.current[typeKey] = {
                onMouseEnter: () => { hoveredTypeRef.current = typeKey; isOverBarRef.current = true },
                onMouseLeave: () => { hoveredTypeRef.current = null; isOverBarRef.current = false }
            }
        }
        return barHandlersRef.current[typeKey]
    }, [])

    if (!data || !chartData.length) return null

    const isPerImage = data.resolution === 'per_image'

    return (
        <Paper elevation={0} sx={{ py: isMobile ? 1.5 : 2, userSelect: 'none', '& :focus': { outline: 'none' }, '& *': { outline: 'none !important' }, boxShadow: '0 2px 4px rgba(0,0,0,0.08)', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'center', fontSize: isMobile ? 14 : undefined }}>
                {isPerImage ? t('pages.dashboard.per_image_title') : t('pages.dashboard.daily_title')}
            </Typography>

            <div ref={containerRef}>
                <ResponsiveContainer width="100%" height={chartHeight || 500}>
                    <BarChart
                        data={chartData}
                        onClick={isMobile ? undefined : handleClick}
                        onMouseMove={isMobile ? undefined : handleMouseMove}
                        onMouseLeave={isMobile ? undefined : handleMouseLeave}
                        margin={{ top: 5, right: isMobile ? 18 : 38, bottom: 5, left: 0 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatXAxis}
                            angle={-45}
                            textAnchor="end"
                            height={isMobile ? 40 : 50}
                            tick={{ fontSize: isMobile ? 9 : 11 }}
                            minTickGap={isMobile ? 15 : 5}
                        />
                        <YAxis tick={{ fontSize: isMobile ? 9 : 11 }} width={isMobile ? 32 : 52} />
                        {!isMobile && (
                            <Tooltip
                                content={<DomTooltip />}
                                cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                                offset={20}
                                wrapperStyle={{ zIndex: 10, pointerEvents: 'none' }}
                            />
                        )}
                        {enabledTypesArray.map(elem => {
                            const handlers = getBarHandlers(elem)
                            return (
                                <Bar
                                    key={elem}
                                    dataKey={elem}
                                    stackId="detections"
                                    fill={getDetectionColor(elem)}
                                    isAnimationActive={false}
                                    onClick={isMobile ? handleBarTap : undefined}
                                    onMouseEnter={isMobile ? undefined : handlers.onMouseEnter}
                                    onMouseLeave={isMobile ? undefined : handlers.onMouseLeave}
                                />
                            )
                        })}
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', fontSize: isMobile ? 10 : undefined }}>
                {isPerImage ? t('pages.dashboard.click_bar_images') : t('pages.dashboard.click_bar_detail')}
            </Typography>
        </Paper>
    )
}
